import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text, SimpleGrid } from '@repletoryreach/sdk-ui';
import { TextInput, Textarea, NumberInput, Switch } from '@repletoryreach/sdk-ui/inputs';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { usePriceLadder } from '../../../../data/useSkuRates';
import { formatInteger, formatMinor } from '../../../../lib/format';
import {
  DEFAULT_MARKUP_PERCENT,
  MAX_MARKUP_PERCENT,
  MINOR_PER_CREDIT,
  entryPlan,
  formatCredits,
  includedCeilingMinor,
  ladderRefusal,
  wholeCredits
} from '../../../../lib/priceLadder';
import { useT, type Translate } from '../../../../i18n/I18nProvider';

/** The allowances a plan carries, in the order they are shown. */
export const LIMIT_KEYS = ['bots', 'numbers', 'team_members', 'flows', 'knowledge_documents'] as const;

/**
 * Whether the business may connect its own WhatsApp account, so Meta bills it
 * directly (5 October 2026). Stored as a limit but meant as a switch: `0` is
 * not included, no ceiling is included. Edited as a switch so nobody types a
 * count that means nothing.
 */
export const OWN_ACCOUNT_KEY = 'own_whatsapp_account';

/** True unless the plan carries an explicit allowance of zero. */
export function ownAccountIncluded(limits: Record<string, number | null | undefined> | null | undefined): boolean {
  return switchIncluded(limits, OWN_ACCOUNT_KEY);
}

/**
 * Extra use (30 October 2026): a business on the plan may keep going after its credit runs out, charged to its card
 * at the plan's prices, up to a monthly limit it sets. A switch like the own WhatsApp account: `0` is not included,
 * no ceiling is included. Growth and Scale have it.
 */
export const EXTRA_USE_KEY = 'extra_use';

/** The Bookings and Tickets counts (7 October 2026), shown under their own heading. */
export const MODULE_LIMIT_KEYS = ['booking_staff', 'booking_services', 'ticket_types', 'ticket_automations'] as const;

/**
 * Whether bookings may sync with an outside calendar. A switch like the own
 * WhatsApp account: `0` is not included, no ceiling is included.
 */
export const CALENDAR_SYNC_KEY = 'calendar_sync';

/** A limit meant as a switch: on unless the plan carries an explicit allowance of zero. */
export function switchIncluded(limits: Record<string, number | null | undefined> | null | undefined, key: string): boolean {
  const v = limits?.[key];
  return !(typeof v === 'number' && v <= 0);
}

const LIMIT_COLS = { base: 1, sm: 2 };

/** Which of the two money fields a refusal belongs under, and the words for it. */
interface MoneyRefusal { field: 'markup' | 'credits'; text: string }

/**
 * The refusals that keep a plan from losing money, in the operator's words and
 * with the service's own numbers. Anything else answers null and is reported as
 * the service worded it.
 */
function moneyRefusal(
  t: Translate,
  err: unknown,
  isEntry: boolean,
  typed: { markup: number | null; creditsMinor: number | null }
): MoneyRefusal | null {
  const refusal = ladderRefusal(err);
  if (!refusal) return null;
  if (refusal.code === 'PLAN_CAN_LOSE_MONEY' && refusal.ceiling_minor != null) {
    return {
      field: 'credits',
      text: t('billing.plans.edit.refusal.canLoseMoney', {
        markup: refusal.markup_percent ?? typed.markup ?? '',
        ceiling: formatInteger(wholeCredits(refusal.ceiling_minor)),
        credits: formatCredits(refusal.included_credits_minor ?? typed.creditsMinor ?? 0)
      })
    };
  }
  if (refusal.code === 'MARKUP_LADDER' && refusal.entry_markup_percent != null) {
    const entry = refusal.entry_markup_percent;
    // The service refuses the ladder as a whole, so the plan on screen is not
    // always the one at fault: say which of the three it is.
    if (isEntry) return { field: 'markup', text: t('billing.plans.edit.refusal.ladderFree', { entry }) };
    if (typed.markup != null && typed.markup > entry) return { field: 'markup', text: t('billing.plans.edit.refusal.ladderPaid', { entry }) };
    return { field: 'markup', text: t('billing.plans.edit.refusal.ladderOther', { entry }) };
  }
  if (refusal.code === 'INVALID_MARKUP') return { field: 'markup', text: t('billing.plans.edit.markupInvalid') };
  return null;
}

/**
 * A plan's words and allowances (what the public price list and the tenant
 * console show) and the two figures that price it: its markup over cost and
 * its included credits. The monthly fee is read-only here on purpose: a
 * different fee is a different plan, so existing subscribers keep what they
 * signed up for.
 *
 * No plan may lose money (owner's ruling, 21 September 2026), so the included
 * credits are held under `fee x (1 + markup)`. The ceiling is worked out as
 * the operator types and Save is held back above it; the service checks again
 * and its refusals land under the field they are about.
 *
 * A changed markup reprices every customer on the plan from that moment, and
 * the free plan's re-lists the price book, so a save that moves either money
 * figure confirms first and says what moves. Words and allowances save as
 * they always did.
 */
export function PlanEditDrawer({ plan, opened, onClose }: { plan: admin.AdminPlan | null; opened: boolean; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  // The plans page has these cached. They say which plan is the entry plan.
  const ladder = usePriceLadder();
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [badge, setBadge] = useState('');
  const [features, setFeatures] = useState('');
  const [limits, setLimits] = useState<Record<string, number | ''>>({});
  const [ownAccount, setOwnAccount] = useState(true);
  const [calendarSync, setCalendarSync] = useState(true);
  const [extraUse, setExtraUse] = useState(false);
  const [sortOrder, setSortOrder] = useState<number | ''>(0);
  const [active, setActive] = useState(true);
  const [markup, setMarkup] = useState<number | ''>('');
  // Typed in whole credits, stored in minor units (100 per credit).
  const [credits, setCredits] = useState<number | ''>('');
  const [refusal, setRefusal] = useState<MoneyRefusal | null>(null);

  useEffect(() => {
    if (!plan || !opened) return;
    setName(plan.name);
    setTagline(plan.tagline ?? '');
    setBadge(plan.badge ?? '');
    setFeatures(plan.features.join('\n'));
    const next: Record<string, number | ''> = {};
    for (const k of [...LIMIT_KEYS, ...MODULE_LIMIT_KEYS]) {
      const v = plan.limits[k];
      next[k] = typeof v === 'number' ? v : '';
    }
    setLimits(next);
    setOwnAccount(ownAccountIncluded(plan.limits));
    setCalendarSync(switchIncluded(plan.limits, CALENDAR_SYNC_KEY));
    setExtraUse(switchIncluded(plan.limits, EXTRA_USE_KEY));
    setSortOrder(plan.sort_order);
    setActive(plan.is_active);
    setMarkup(plan.markup_percent ?? DEFAULT_MARKUP_PERCENT);
    setCredits(plan.included_credits_minor / MINOR_PER_CREDIT);
    setRefusal(null);
  }, [plan, opened]);

  const fee = plan?.monthly_amount_minor ?? 0;
  // The cheapest plan on sale sets the pay-as-you-go price. A free plan is
  // that plan, which also covers the moment before the list has been read.
  const isEntry = plan != null && (ladder.all.length > 0 ? entryPlan(ladder.all)?.id === plan.id : fee <= 0);
  // The service holds a markup as a whole number, so 70.5 is not one.
  const markupPercent = typeof markup === 'number' && Number.isInteger(markup) && markup >= 0 && markup <= MAX_MARKUP_PERCENT ? markup : null;
  const creditsMinor = typeof credits === 'number' ? Math.round(credits * MINOR_PER_CREDIT) : null;
  // Recomputed on every keystroke: lowering the markup lowers the ceiling.
  const ceilingMinor = markupPercent == null ? null : includedCeilingMinor(fee, markupPercent);
  const overCeiling = creditsMinor != null && ceilingMinor != null && creditsMinor > ceilingMinor;
  const moneyValid = markupPercent != null && creditsMinor != null && !overCeiling;

  const save = useMutation({
    mutationFn: (req: admin.UpdatePlanRequest) => admin.updatePlan(plan!.id, req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
      // A plan's markup prices its messages, and the free plan's re-lists the
      // price book, so what each plan pays is stale too.
      qc.invalidateQueries({ queryKey: ['admin', 'sku-rates'] });
      notifySuccess(t('billing.plans.edit.saved', { name: res.data.name }), t('billing.plans.title'));
      onClose();
    },
    onError: (e) => {
      const refused = moneyRefusal(t, e, isEntry, { markup: markupPercent, creditsMinor });
      setRefusal(refused);
      // The money fields sit at the top of a long drawer and Save at the
      // bottom, so the toast says it too.
      notifyError(refused?.text ?? (e instanceof Error ? e.message : t('common.unknownError')), t('billing.plans.edit.failed'));
    }
  });

  function submit() {
    if (!plan || markupPercent == null || creditsMinor == null || overCeiling) return;
    const limitsOut: Record<string, number | null> = {};
    for (const k of [...LIMIT_KEYS, ...MODULE_LIMIT_KEYS]) limitsOut[k] = typeof limits[k] === 'number' ? (limits[k] as number) : null;
    limitsOut[OWN_ACCOUNT_KEY] = ownAccount ? null : 0;
    limitsOut[CALENDAR_SYNC_KEY] = calendarSync ? null : 0;
    limitsOut[EXTRA_USE_KEY] = extraUse ? null : 0;
    // The two money fields go only when they were changed. The service checks
    // the ceiling and the whole ladder whenever either arrives, so a plan whose
    // words are being corrected is not refused over a ladder nobody touched.
    const money: Pick<admin.UpdatePlanRequest, 'markup_percent' | 'included_credits_minor'> = {};
    if (markupPercent !== plan.markup_percent) money.markup_percent = markupPercent;
    if (creditsMinor !== plan.included_credits_minor) money.included_credits_minor = creditsMinor;
    setRefusal(null);
    const req: admin.UpdatePlanRequest = {
      name,
      tagline,
      badge,
      features: features.split('\n').map((l) => l.trim()).filter(Boolean),
      limits: limitsOut,
      sort_order: typeof sortOrder === 'number' ? sortOrder : 0,
      is_active: active,
      ...money
    };
    if (money.markup_percent == null && money.included_credits_minor == null) {
      save.mutate(req);
      return;
    }
    const markupFrom = plan.markup_percent ?? DEFAULT_MARKUP_PERCENT;
    openConfirmModal({
      title: t('billing.plans.edit.confirm.title', { name: plan.name }),
      children: (
        <Stack gap="sm">
          {money.markup_percent != null && (
            <Text role="bodySmall">
              {isEntry
                ? t('billing.plans.edit.confirm.markupEntry', { name: plan.name, from: markupFrom, to: money.markup_percent })
                : t('billing.plans.edit.confirm.markup', { name: plan.name, from: markupFrom, to: money.markup_percent })}
            </Text>
          )}
          {money.included_credits_minor != null && (
            <Text role="bodySmall">
              {t('billing.plans.edit.confirm.credits', { from: formatCredits(plan.included_credits_minor), to: formatCredits(money.included_credits_minor) })}
            </Text>
          )}
        </Stack>
      ),
      labels: { confirm: t('billing.plans.edit.confirm.submit'), cancel: t('action.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => save.mutate(req)
    });
  }

  const markupError = refusal?.field === 'markup'
    ? refusal.text
    : markupPercent == null ? t('billing.plans.edit.markupInvalid') : undefined;

  const creditsHint = fee <= 0
    ? t('billing.plans.edit.creditsHintFree')
    : ceilingMinor == null || markupPercent == null
      ? t('billing.plans.edit.creditsHintNoMarkup')
      : t('billing.plans.edit.creditsHint', { markup: markupPercent, ceiling: formatInteger(wholeCredits(ceilingMinor)) });

  const creditsError = refusal?.field === 'credits'
    ? refusal.text
    : creditsMinor == null
      ? t('billing.plans.edit.creditsRequired')
      : !overCeiling || ceilingMinor == null || markupPercent == null
        ? undefined
        : fee <= 0
          ? t('billing.plans.edit.creditsOverFree')
          : t('billing.plans.edit.creditsOver', { credits: formatCredits(creditsMinor), markup: markupPercent, ceiling: formatInteger(wholeCredits(ceilingMinor)) });

  return (
    <Drawer opened={opened} onClose={onClose} title={plan ? t('billing.plans.edit.title', { name: plan.name }) : ''} position="right" size="md">
      {plan && (
        <Stack gap="md">
          <Text role="bodySmall" c="var(--rr-color-text-muted)">
            {t('billing.plans.edit.priceNote', { monthly: formatMinor(plan.monthly_amount_minor, plan.currency) })}
          </Text>
          <Stack gap="sm">
            <Text role="bodySmall" fw={600}>{t('billing.plans.edit.pricing')}</Text>
            <Stack gap="md">
              <NumberInput
                size="sm"
                label={t('billing.plans.edit.markup')}
                value={markup}
                onChange={(v) => { setMarkup(typeof v === 'number' ? v : ''); setRefusal(null); }}
                min={0}
                max={MAX_MARKUP_PERCENT}
                description={t('billing.plans.edit.markupHint')}
                error={markupError}
              />
              <NumberInput
                size="sm"
                label={t('billing.plans.edit.credits')}
                value={credits}
                // No `max`: the input would clamp to it silently, and the point
                // is for the operator to see that the figure is over and why.
                onChange={(v) => { setCredits(typeof v === 'number' ? v : ''); setRefusal(null); }}
                min={0}
                description={creditsHint}
                error={creditsError}
              />
            </Stack>
          </Stack>
          <TextInput label={t('billing.plans.edit.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} required />
          <TextInput label={t('billing.plans.edit.tagline')} value={tagline} onChange={(e) => setTagline(e.currentTarget.value)} description={t('billing.plans.edit.taglineHint')} />
          <TextInput label={t('billing.plans.edit.badge')} value={badge} onChange={(e) => setBadge(e.currentTarget.value)} description={t('billing.plans.edit.badgeHint')} />
          <Textarea
            label={t('billing.plans.edit.features')}
            value={features}
            onChange={(e) => setFeatures(e.currentTarget.value)}
            autosize
            minRows={6}
            maxRows={16}
            description={t('billing.plans.edit.featuresHint')}
          />
          <Stack gap="xs">
            <Text role="bodySmall" fw={600}>{t('billing.plans.edit.limits')}</Text>
            <SimpleGrid cols={LIMIT_COLS} spacing="sm">
              {LIMIT_KEYS.map((k) => (
                <NumberInput
                  key={k}
                  size="sm"
                  label={t(`billing.plans.limit.${k}`)}
                  value={limits[k] ?? ''}
                  onChange={(v) => setLimits((prev) => ({ ...prev, [k]: typeof v === 'number' ? v : '' }))}
                  min={0}
                  placeholder={t('billing.plans.unlimited')}
                />
              ))}
            </SimpleGrid>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.plans.edit.limitsHint')}</Text>
            <Switch
              label={t('billing.plans.limit.own_whatsapp_account')}
              checked={ownAccount}
              onChange={setOwnAccount}
              description={t('billing.plans.edit.ownAccountHint')}
            />
            <Switch
              label={t('billing.plans.limit.extra_use')}
              checked={extraUse}
              onChange={setExtraUse}
              description={t('billing.plans.edit.extraUseHint')}
            />
          </Stack>
          <Stack gap="xs">
            <Text role="bodySmall" fw={600}>{t('billing.plans.edit.moduleLimits')}</Text>
            <SimpleGrid cols={LIMIT_COLS} spacing="sm">
              {MODULE_LIMIT_KEYS.map((k) => (
                <NumberInput
                  key={k}
                  size="sm"
                  label={t(`billing.plans.limit.${k}`)}
                  value={limits[k] ?? ''}
                  onChange={(v) => setLimits((prev) => ({ ...prev, [k]: typeof v === 'number' ? v : '' }))}
                  min={0}
                  placeholder={t('billing.plans.unlimited')}
                />
              ))}
            </SimpleGrid>
            <Switch
              label={t('billing.plans.limit.calendar_sync')}
              checked={calendarSync}
              onChange={setCalendarSync}
              description={t('billing.plans.edit.calendarSyncHint')}
            />
          </Stack>
          <Group gap="md" align="flex-start" wrap="wrap">
            <NumberInput
              size="sm"
              style={{ width: 160 }}
              label={t('billing.plans.edit.sortOrder')}
              value={sortOrder}
              onChange={(v) => setSortOrder(typeof v === 'number' ? v : '')}
              min={0}
              description={t('billing.plans.edit.sortOrderHint')}
            />
            <Switch
              checked={active}
              onChange={setActive}
              label={t('billing.plans.edit.offered')}
              description={t('billing.plans.edit.offeredHint')}
            />
          </Group>
          {!moneyValid && (
            <Text role="caption" ta="right" c="var(--rr-color-error)">{t('billing.plans.edit.blocked')}</Text>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
            <Button loading={save.isPending} disabled={!name.trim() || !moneyValid} onClick={submit}>{t('action.save')}</Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
